import React from 'react';

// Import config and utils
import { useIntl } from '../../util/reactIntl';
import {
  SCHEMA_TYPE_ENUM,
  SCHEMA_TYPE_MULTI_ENUM,
  SCHEMA_TYPE_TEXT,
  SCHEMA_TYPE_LONG,
  SCHEMA_TYPE_BOOLEAN,
  SCHEMA_TYPE_YOUTUBE,
} from '../../util/types';
import {
  required,
  nonEmptyArray,
  validateInteger,
  validateYoutubeURL,
} from '../../util/validators';
// Import shared components
import { FieldCheckboxGroup, FieldSelect, FieldTextInput, FieldBoolean } from '../../components';
// Import modules from this directory
import css from './CustomExtendedDataField.module.css';
import classNames from 'classnames';

const createFilterOptions = options => options.map(o => ({ key: `${o.option}`, label: o.label }));

const getLabel = fieldConfig => fieldConfig?.saveConfig?.label || fieldConfig?.label;

// Helper: ensure we have a plain string label
const getLabelText = label => (typeof label === 'string' ? label : '');

const buildRequiredMessage = (label, requiredMessage, defaultRequiredMessage) => {
  const labelText = getLabelText(label);

  if (requiredMessage) return requiredMessage;

  if (labelText) return `${labelText} is required.`;

  // 3. Fall back to generic default ("Required", etc.)
  if (defaultRequiredMessage) return defaultRequiredMessage;

  return 'This field is required.';
};

const buildAddPlaceholder = (label, placeholderMessage, defaultMessageId, intl) => {
  if (placeholderMessage) return placeholderMessage;

  const labelText = getLabelText(label);
  if (labelText) {
    return `Add ${labelText.toLowerCase()}`;
  }

  // Fallback to existing i18n placeholder if we don't have a string label
  return intl.formatMessage({ id: defaultMessageId });
};

const showAsteriskLabel = label => {
  return label + ' ' + '*';
};

const CustomFieldEnum = props => {
  const {
    name,
    fieldConfig,
    defaultRequiredMessage,
    formId,
    intl,
    isEditListingCustomFlds,
    showAsterisk,
  } = props;
  const { enumOptions = [], saveConfig } = fieldConfig || {};
  const { placeholderMessage, isRequired, requiredMessage } = saveConfig || {};
  const label = getLabel(fieldConfig);

  const requiredMsg = buildRequiredMessage(label, requiredMessage, defaultRequiredMessage);
  const validateMaybe = isRequired ? { validate: required(requiredMsg) } : {};

  const placeholder =
    placeholderMessage ||
    intl.formatMessage({ id: 'CustomExtendedDataField.placeholderSingleSelect' });

  const filterOptions = createFilterOptions(enumOptions);

  return filterOptions ? (
    <FieldSelect
      className={classNames(css.customField, isEditListingCustomFlds ? css.formFld : null)}
      name={name}
      id={formId ? `${formId}.${name}` : name}
      label={showAsterisk ? showAsteriskLabel(label) : label}
      {...validateMaybe}
    >
      <option disabled value="">
        {placeholder}
      </option>
      {filterOptions.map(optionConfig => {
        const key = optionConfig.key;
        return (
          <option key={key} value={key}>
            {optionConfig.label}
          </option>
        );
      })}
    </FieldSelect>
  ) : null;
};

const CustomFieldMultiEnum = props => {
  const {
    name,
    fieldConfig,
    defaultRequiredMessage,
    formId,
    isEditListingCustomFlds,
    showAsterisk,
  } = props;
  const { enumOptions = [], saveConfig } = fieldConfig || {};
  const { isRequired, requiredMessage } = saveConfig || {};
  const label = getLabel(fieldConfig);
  const validateMaybe = isRequired
    ? { validate: nonEmptyArray(requiredMessage || defaultRequiredMessage) }
    : {};

  return enumOptions ? (
    <FieldCheckboxGroup
      className={classNames(css.customField, isEditListingCustomFlds ? css.formFld : null)}
      id={formId ? `${formId}.${name}` : name}
      name={name}
      label={showAsterisk ? showAsteriskLabel(label) : label}
      options={createFilterOptions(enumOptions)}
      {...validateMaybe}
    />
  ) : null;
};

const CustomFieldText = props => {
  const {
    name,
    fieldConfig,
    defaultRequiredMessage,
    formId,
    intl,
    isEditListingCustomFlds,
    showAsterisk,
  } = props;
  const { placeholderMessage, isRequired, requiredMessage } = fieldConfig?.saveConfig || {};
  const label = getLabel(fieldConfig);

  const requiredMsg = buildRequiredMessage(label, requiredMessage, defaultRequiredMessage);
  const validateMaybe = isRequired ? { validate: required(requiredMsg) } : {};

  const placeholder = buildAddPlaceholder(
    label,
    placeholderMessage,
    'CustomExtendedDataField.placeholderText',
    intl
  );

  return (
    <FieldTextInput
      className={classNames(css.customField, isEditListingCustomFlds ? css.formFld : null)}
      id={formId ? `${formId}.${name}` : name}
      name={name}
      type="text"
      label={showAsterisk ? showAsteriskLabel(label) : label}
      placeholder={placeholder}
      {...validateMaybe}
    />
  );
};

const CustomFieldLong = props => {
  const {
    name,
    fieldConfig,
    defaultRequiredMessage,
    formId,
    intl,
    isEditListingCustomFlds,
    showAsterisk,
  } = props;
  const { minimum, maximum, saveConfig } = fieldConfig;
  const { placeholderMessage, isRequired, requiredMessage } = saveConfig || {};
  const label = getLabel(fieldConfig);

  const placeholder = buildAddPlaceholder(
    label,
    placeholderMessage,
    'CustomExtendedDataField.placeholderLong',
    intl
  );

  const numberTooSmallMessage = intl.formatMessage(
    { id: 'CustomExtendedDataField.numberTooSmall' },
    { min: minimum }
  );
  const numberTooBigMessage = intl.formatMessage(
    { id: 'CustomExtendedDataField.numberTooBig' },
    { max: maximum }
  );

  const requiredMsg = buildRequiredMessage(label, requiredMessage, defaultRequiredMessage);

  // Field with schema type 'long' will always be validated against min & max
  const validate = (value, min, max) => {
    return isRequired && value == null
      ? requiredMsg
      : validateInteger(value, max, min, numberTooSmallMessage, numberTooBigMessage);
  };

  return (
    <FieldTextInput
      className={classNames(css.customField, isEditListingCustomFlds ? css.formFld : null)}
      id={formId ? `${formId}.${name}` : name}
      name={name}
      type="number"
      step="1"
      parse={value => {
        const parsed = Number.parseInt(value, 10);
        return Number.isNaN(parsed) ? null : parsed;
      }}
      label={showAsterisk ? showAsteriskLabel(label) : label}
      placeholder={placeholder}
      validate={value => validate(value, minimum, maximum)}
      onWheel={e => {
        if (e.target === document.activeElement) {
          e.target.blur();
          setTimeout(() => {
            e.target.focus();
          }, 0);
        }
      }}
    />
  );
};

const CustomFieldBoolean = props => {
  const {
    name,
    fieldConfig,
    defaultRequiredMessage,
    formId,
    intl,
    isEditListingCustomFlds,
    showAsterisk,
  } = props;
  const { placeholderMessage, isRequired, requiredMessage } = fieldConfig?.saveConfig || {};
  const label = getLabel(fieldConfig);
  const validateMaybe = isRequired
    ? { validate: required(requiredMessage || defaultRequiredMessage) }
    : {};
  const placeholder =
    placeholderMessage || intl.formatMessage({ id: 'CustomExtendedDataField.placeholderBoolean' });

  return (
    <FieldBoolean
      className={classNames(css.customField, isEditListingCustomFlds ? css.formFld : null)}
      id={formId ? `${formId}.${name}` : name}
      name={name}
      label={showAsterisk ? showAsteriskLabel(label) : label}
      placeholder={placeholder}
      {...validateMaybe}
    />
  );
};

const CustomFieldYoutube = props => {
  const {
    name,
    fieldConfig,
    defaultRequiredMessage,
    formId,
    intl,
    isEditListingCustomFlds,
    showAsterisk,
  } = props;
  const { placeholderMessage, isRequired, requiredMessage } = fieldConfig?.saveConfig || {};
  const label = getLabel(fieldConfig);
  const placeholder =
    placeholderMessage ||
    intl.formatMessage({ id: 'CustomExtendedDataField.placeholderYoutubeVideoURL' });

  const notValidUrlMessage = intl.formatMessage({
    id: 'CustomExtendedDataField.notValidYoutubeVideoURL',
  });

  const validate = value => {
    const requiredMsg = requiredMessage || defaultRequiredMessage;
    return isRequired && value == null
      ? requiredMsg
      : validateYoutubeURL(value, notValidUrlMessage);
  };

  return (
    <FieldTextInput
      className={classNames(css.customField, isEditListingCustomFlds ? css.formFld : null)}
      id={formId ? `${formId}.${name}` : name}
      name={name}
      type="text"
      label={showAsterisk ? showAsteriskLabel(label) : label}
      placeholder={placeholder}
      validate={value => validate(value)}
    />
  );
};

/**
 * Return Final Form field for each configuration according to schema type.
 *
 * These custom extended data fields are for generating input fields from configuration defined
 * in marketplace-custom-config.js. Other panels in EditListingWizard might add more extended data
 * fields (e.g. shipping fee), but these are independently customizable.
 *
 * @param {Object} props should contain fieldConfig that defines schemaType, enumOptions?, and
 * saveConfig for the field.
 */
const CustomExtendedDataField = props => {
  const intl = useIntl();
  const { enumOptions = [], schemaType } = props?.fieldConfig || {};
  const renderFieldComponent = (FieldComponent, props) => <FieldComponent {...props} intl={intl} />;

  return schemaType === SCHEMA_TYPE_ENUM && enumOptions
    ? renderFieldComponent(CustomFieldEnum, props)
    : schemaType === SCHEMA_TYPE_MULTI_ENUM && enumOptions
    ? renderFieldComponent(CustomFieldMultiEnum, props)
    : schemaType === SCHEMA_TYPE_TEXT
    ? renderFieldComponent(CustomFieldText, props)
    : schemaType === SCHEMA_TYPE_LONG
    ? renderFieldComponent(CustomFieldLong, props)
    : schemaType === SCHEMA_TYPE_BOOLEAN
    ? renderFieldComponent(CustomFieldBoolean, props)
    : schemaType === SCHEMA_TYPE_YOUTUBE
    ? renderFieldComponent(CustomFieldYoutube, props)
    : null;
};

export default CustomExtendedDataField;
